'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import {
  TraceViewSettingsContext,
  type TraceSourceFilter,
  type TraceViewSettings,
} from '@terra/ui-design-system';
import { useTraceViewSettingsStore } from '../store/traceViewSettingsStore';

type TraceViewSettingsContextType = {
  settings: TraceViewSettings;
  setShowHttpViewGlobal: (show: boolean) => void;
  setCardHttpView: (cardId: string, showHttp: boolean) => void;
  shouldShowHttpView: (cardId: string) => boolean;
  setShowDetailedView: (show: boolean) => void;
  setIsPlanExpanded: (expanded: boolean) => void;
  setShowCondensedView: (show: boolean) => void;
  setSourceFilter: (filter: TraceSourceFilter) => void;
  setShowHiddenSteps: (show: boolean) => void;
};

type TraceViewSettingsStoreProviderProps = {
  children: ReactNode;
};

export function TraceViewSettingsStoreProvider({
  children,
}: TraceViewSettingsStoreProviderProps) {
  const showHttpViewGlobal = useTraceViewSettingsStore(
    (state) => state.showHttpViewGlobal,
  );
  const httpViewPerCard = useTraceViewSettingsStore(
    (state) => state.httpViewPerCard,
  );
  const showDetailedView = useTraceViewSettingsStore(
    (state) => state.showDetailedView,
  );
  const isPlanExpanded = useTraceViewSettingsStore(
    (state) => state.isPlanExpanded,
  );
  const showCondensedView = useTraceViewSettingsStore(
    (state) => state.showCondensedView,
  );
  const sourceFilter = useTraceViewSettingsStore((state) => state.sourceFilter);
  const showHiddenSteps = useTraceViewSettingsStore(
    (state) => state.showHiddenSteps,
  );

  const setShowHttpViewGlobal = useTraceViewSettingsStore(
    (state) => state.setShowHttpViewGlobal,
  );
  const setCardHttpView = useTraceViewSettingsStore(
    (state) => state.setCardHttpView,
  );
  const setShowDetailedView = useTraceViewSettingsStore(
    (state) => state.setShowDetailedView,
  );
  const setIsPlanExpanded = useTraceViewSettingsStore(
    (state) => state.setIsPlanExpanded,
  );
  const setShowCondensedView = useTraceViewSettingsStore(
    (state) => state.setShowCondensedView,
  );
  const setSourceFilter = useTraceViewSettingsStore(
    (state) => state.setSourceFilter,
  );
  const setShowHiddenSteps = useTraceViewSettingsStore(
    (state) => state.setShowHiddenSteps,
  );

  const settings = useMemo<TraceViewSettings>(
    () => ({
      showHttpViewGlobal,
      httpViewPerCard,
      showDetailedView,
      isPlanExpanded,
      showCondensedView,
      sourceFilter,
      showHiddenSteps,
    }),
    [
      showHttpViewGlobal,
      httpViewPerCard,
      showDetailedView,
      isPlanExpanded,
      showCondensedView,
      sourceFilter,
      showHiddenSteps,
    ],
  );

  const shouldShowHttpView = useCallback(
    (cardId: string) => {
      const cardHttpViewOverride = httpViewPerCard[cardId];
      if (cardHttpViewOverride !== undefined) {
        return cardHttpViewOverride;
      }
      return showHttpViewGlobal;
    },
    [httpViewPerCard, showHttpViewGlobal],
  );

  const value = useMemo<TraceViewSettingsContextType>(
    () => ({
      settings,
      setShowHttpViewGlobal,
      setCardHttpView,
      shouldShowHttpView,
      setShowDetailedView,
      setIsPlanExpanded,
      setShowCondensedView,
      setSourceFilter,
      setShowHiddenSteps,
    }),
    [
      settings,
      setShowHttpViewGlobal,
      setCardHttpView,
      shouldShowHttpView,
      setShowDetailedView,
      setIsPlanExpanded,
      setShowCondensedView,
      setSourceFilter,
      setShowHiddenSteps,
    ],
  );

  return (
    <TraceViewSettingsContext.Provider value={value}>
      {children}
    </TraceViewSettingsContext.Provider>
  );
}
